'use client';

import './sac-list-spotlight.scss';

import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRandomChildren } from '@sola/proxy-sdk/children.ts';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import type { SacFilterProps } from '@sola/schema/lib/schema.ts';
import { getHeaderDisplayLevel } from '@sola/utils/assembly.ts';

import { FeatureBase } from '../../../molecules/feature-base/feature-base.tsx';
import { SacCard } from '../../../molecules/sac-card/sac-card.tsx';
import { getBaseSizeProps } from '../../../utils/text-utils.tsx';
import { ChildBioSkeleton } from '../../child-bio/child-bio-skeleton';

export const SacSpotlight: React.FC<SacFilterProps> = ({
  seoLevel = 2,
  title,
  body,
  ...props
}) => {
  const { t } = useTranslation(['bio', 'core']);
  const [childData, setChildData] = useState<ChildAvailabilityItem>();

  const headerDisplayLevel = getHeaderDisplayLevel(seoLevel);

  const { mergeFieldData: { richmixChildData } = {} } = props || {};

  useEffect(() => {
    if (richmixChildData) {
      setChildData(richmixChildData);
      return;
    }

    const getChildData = async () => {
      const response = await getRandomChildren();
      setChildData(response?.children[0]);
    };

    getChildData();
  }, []);

  return (
    <section className="SacListSpotlight component">
      <Suspense fallback={<ChildBioSkeleton />}>
        <div className="container md:grid flex flex-col md:grid-cols-2 items-center gap-comfy-xl">
          <div className="flex flex-col gap-comfy-sm md:gap-comfy-lg">
            <FeatureBase
              {...getBaseSizeProps(headerDisplayLevel)}
              seoLevel={seoLevel}
              titleSize={seoLevel === 1 ? 'xxl' : 'xl'}
              title={title}
              body={body}
              className="pb-comfy-sm"
              variantResponsive
              inline
              primaryCTA={
                childData && {
                  title: t('bio:title', {
                    childName: childData.name.preferred,
                  }).replaceAll(/[!.?,:;]/g, ''),
                  link: `/sponsor-a-child/child-bio?gid=${childData.globalId}${childData.sourceCode ? '&source=' + childData.sourceCode : ''}`,
                }
              }
            />
          </div>
          <aside className="SacListSpotlight__child-container w-full">
            {childData && <SacCard {...childData} />}
          </aside>
        </div>
      </Suspense>
    </section>
  );
};
